import { useAndie } from '@/providers/andie-provider';
import { useAndieChatControls } from '@/providers/andie-chat-provider';
import { AutoAwesomeRounded } from '@mui/icons-material';
import { Theme, useMediaQuery } from '@mui/material';
import Kbd from '../common/Kbd';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import AndieButton from './AndieButton';
import AndieIconAnimated from './chat/AndieIconAnimated';

const HIGHLIGHT_STORAGE_KEY = 'andie-nav-button-highlight-dismissed';

// Local date (YYYY-MM-DD) so the colorful highlight resets once per calendar day.
const today = () => new Date().toLocaleDateString('en-CA');

const AndieNavButton = () => {
    const { open, setOpen } = useAndie();
    const { isLoading } = useAndieChatControls();
    const { t } = useTranslation();
    const isSmallScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

    // Highlight (colorful) until the user clicks the button for the first time today.
    const [highlight, setHighlight] = useState(() => localStorage.getItem(HIGHLIGHT_STORAGE_KEY) !== today());

    const handleClick = () => {
        if (highlight) {
            localStorage.setItem(HIGHLIGHT_STORAGE_KEY, today());
            setHighlight(false);
        }
        setOpen(!open);
    };

    // Double-click resets the daily highlight so it lights up again immediately.
    const handleDoubleClick = () => {
        localStorage.removeItem(HIGHLIGHT_STORAGE_KEY);
        setHighlight(true);
    };

    return (
        <AndieButton
            variant="contained"
            size="small"
            // Toggling the drawer is always safe, and locking the only visible way back into a
            // running conversation is the worst moment to lock it. The animated icon carries the
            // run state instead of a spinner.
            allowWhileBusy
            startIcon={isLoading ? <AndieIconAnimated size={20} /> : <AutoAwesomeRounded fontSize="small" />}
            title={
                isLoading ? (
                    t('andie.thinking')
                ) : (
                    <Trans i18nKey="andie.chat-button.tooltip" components={{ shortcut: <Kbd /> }} />
                )
            }
            onClick={handleClick}
            onDoubleClick={handleDoubleClick}
            sx={
                !highlight || open
                    ? null
                    : {
                          color: '#fff',
                          // Filled, animated gradient (overrides AndieButton's gradient-border look)
                          // so the global entry point clearly stands out in the top bar.
                          background: (theme: Theme) =>
                              `linear-gradient(60deg, ${theme.palette.andie.gradient.join(', ')})`,
                          backgroundSize: '250% 250%',
                          backgroundClip: 'border-box',
                          backgroundOrigin: 'border-box',
                          boxShadow: (theme: Theme) => `0 2px 10px 0 ${theme.palette.andie.gradient[0]}55`,
                          // The label is rendered inside a MUI Typography whose variant sets its own
                          // font-weight, so bold has to be applied here rather than on the button.
                          '& .MuiTypography-root': { fontWeight: 700 },
                          '& .MuiButton-startIcon': { color: '#fff' },
                          '&:hover': {
                              border: 'none',
                              filter: 'brightness(1.06)',
                              boxShadow: (theme: Theme) =>
                                  `0 4px 16px 0 ${theme.palette.andie.gradient[0]}77`,
                          },
                      }
            }
        >
            {!isSmallScreen ? t('andie.chat-button.text') : null}
        </AndieButton>
    );
};
export default AndieNavButton;
